import React, { useEffect, useState, useRef } from 'react';

type BulletListProps = {
  items: string[];
};

export const BulletList: React.FC<BulletListProps> = ({ items }) => {
  const [visibleItems, setVisibleItems] = useState<boolean[]>(Array(items.length).fill(false));
  const itemRefs = useRef<(HTMLLIElement | null)[]>([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = itemRefs.current.findIndex((ref) => ref === entry.target);
            if (index !== -1) {
              setVisibleItems((prev) => {
                const newState = [...prev];
                newState[index] = true;
                return newState;
              });
              observer.unobserve(entry.target);
            }
          }
        });
      },
      { threshold: 0.3 }
    );

    itemRefs.current.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => {
      itemRefs.current.forEach((ref) => {
        if (ref) observer.unobserve(ref);
      });
    };
  }, [items.length]);

  return (
    <ul className="list-disc pl-6 my-6 space-y-3">
      {items.map((item, index) => (
        <li
          key={index}
          ref={(el) => (itemRefs.current[index] = el)}
          className={`text-zinc-700 dark:text-zinc-300 transform transition-all duration-700 ${
            visibleItems[index]
              ? 'translate-y-0 opacity-100'
              : 'translate-y-4 opacity-0'
          }`}
        >
          {item}
        </li>
      ))}
    </ul>
  );
};
